import { useEffect } from 'react';
import LoadingSpinner from '../components/common/LoadingSpinner';

const FUNNEL_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/funnel/event`;

/**
 * Seed-user entry gate (2026-08-20 pilot).
 *
 * Paul's Keyhole/UAT build (orientation → keyhole → novara_uat) is served static at
 * `/keyhole/*` on the same origin. Its FC assessment posts to `/api/ccs/fc-attempt` with
 * same-origin credentials — but the build doesn't log anyone in, so hit cold it 403s and
 * drops to its client-side local-fallback (the visible banner, no data captured).
 *
 * This route fixes that WITHOUT touching Paul's build: it sits behind the app's own auth +
 * participation gates (the wrappers in App.js), so by the time it renders, the user is
 * logged in and participation-permitted — i.e. the session cookie is set. It then forwards
 * into Paul's experience on the same origin, so the cookie carries and the FC runs
 * authenticated: no fallback banner, and every placement is captured server-side.
 *
 * Whole cohort is the invited Path A, so the funnel path is fixed to A.
 */
export default function KeyholeEntry() {
  useEffect(() => {
    // Record the funnel arrival before we leave the SPA for Paul's static build. sendBeacon
    // survives the navigation, so it can't block or be cancelled by the redirect below
    // (fire-and-forget; any failure is ignored). This is the entry signal into the Keyhole flow;
    // watch_start + the debrief/constellation dwell events are emitted inside Paul's build.
    let clientId;
    try {
      clientId = localStorage.getItem('novara_client_id');
      if (!clientId) {
        clientId = window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `c_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        localStorage.setItem('novara_client_id', clientId);
      }
    } catch (_err) {
      clientId = undefined; // storage blocked — still send the event, just without a client id
    }
    // sendBeacon so the event survives the navigation below (no axios import — keeps this
    // redirect-only page dependency-light); no-op where sendBeacon is unavailable.
    try {
      if (navigator.sendBeacon) {
        const body = JSON.stringify({
          event_name: 'orientation_view',
          path: 'A',
          client_id: clientId ? clientId.slice(0, 64) : undefined,
        });
        navigator.sendBeacon(FUNNEL_URL, new Blob([body], { type: 'application/json' }));
      }
    } catch (_err) {
      /* fire-and-forget — never block the redirect */
    }

    // Leave the SPA for the static experience (full navigation, not React Router).
    window.location.replace('/keyhole/orientation.html?path=A');
  }, []);
  // Brief — the redirect fires on mount; show a spinner in case of any delay.
  return <LoadingSpinner />;
}
